import { useCallback, useEffect, useRef, useState } from 'react';

import type { SSEMessage } from '@/_shared/types';

import { applySSEEvent } from './sse-reducer';

interface UseSSEOptions {
  /** Called for each incoming SSE event (in the EventSource listener, not a React effect). */
  onEvent?: (eventType: string, data: Record<string, unknown>) => void;
  /** Called when the SSE connection drops (error + disconnect). */
  onDisconnect?: () => void;
}

interface UseSSEReturn {
  messages: SSEMessage[];
  connected: boolean;
  error: string | null;
  clearMessages: () => void;
  reconnect: () => void;
  addMessage: (msg: SSEMessage) => void;
  removeLastUserMessage: (content: string) => void;
  initializeMessages: (msgs: SSEMessage[]) => void;
  /**
   * Optimistically apply a turn_aborted event locally without waiting for SSE.
   * Idempotent: if no matching turn_start exists, messages are unchanged.
   */
  forceTurnAborted: (turnId: string, userMessage: string) => void;
}

const SSE_EVENT_TYPES = [
  'user_message',
  'assistant',
  'tool_use',
  'tool_result',
  'ui_message',
  'result',
  'turn_start',
  'turn_complete',
  'turn_aborted',
  'error',
] as const;

const MAX_RETRIES = 10;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30_000;

export function useSSE(streamUrl: string | null, options?: UseSSEOptions): UseSSEReturn {
  const [messages, setMessages] = useState<SSEMessage[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [connectTrigger, setConnectTrigger] = useState(0);

  // Strict Mode resilience: snapshot messages on cleanup, restore on remount
  // with the same URL to prevent double-replay from mount→unmount→remount.
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  });
  const baseMessagesRef = useRef<SSEMessage[]>([]);
  const prevUrlRef = useRef<string | null>(null);

  // Stable refs for callbacks to avoid re-triggering the effect
  const onEventRef = useRef(options?.onEvent);
  const onDisconnectRef = useRef(options?.onDisconnect);
  useEffect(() => {
    onEventRef.current = options?.onEvent;
  });
  useEffect(() => {
    onDisconnectRef.current = options?.onDisconnect;
  });

  useEffect(() => {
    if (!streamUrl) return;

    // Strict Mode: if we're remounting with the same URL, restore the snapshot
    // taken during the previous cleanup instead of replaying from scratch.
    if (streamUrl === prevUrlRef.current && baseMessagesRef.current.length > 0) {
      setMessages(baseMessagesRef.current);
    }
    prevUrlRef.current = streamUrl;

    // Reset retry and error state — a new URL means a new turn, which deserves
    // a fresh retry budget and clean error slate. Without this, a previous
    // turn's retry count and "Connection lost" error carry over, causing the
    // new turn to exhaust retries prematurely or immediately clear pendingTurnId.
    retryCountRef.current = 0;
    setError(null);
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    function scheduleRetry(): void {
      if (retryCountRef.current >= MAX_RETRIES) {
        setError(`Connection lost after ${MAX_RETRIES} attempts`);
        return;
      }
      retryCountRef.current += 1;
      const attempt = retryCountRef.current;
      const delay = Math.min(BASE_DELAY_MS * 2 ** (attempt - 1), MAX_DELAY_MS);
      setError(`Reconnecting (${attempt}/${MAX_RETRIES})...`);
      retryTimeoutRef.current = setTimeout(openConnection, delay);
    }

    function openConnection(): void {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      let disconnected = false;
      // Set when we receive turn_complete — the server will close the connection
      // after this event, so the resulting onerror should NOT trigger a retry.
      let cleanTerminal = false;

      function handleDisconnect(): void {
        if (disconnected) return;
        disconnected = true;
        eventSource.close();
        eventSourceRef.current = null;
        setConnected(false);

        // If the server closed after turn_complete, this is expected — don't retry.
        if (cleanTerminal) return;

        onDisconnectRef.current?.();
        scheduleRetry();
      }

      const eventSource = new EventSource(streamUrl!, {
        withCredentials: true,
      });
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        setConnected(true);
        setError(null);
        retryCountRef.current = 0;
      };

      eventSource.onerror = () => {
        handleDisconnect();
      };

      for (const eventType of SSE_EVENT_TYPES) {
        eventSource.addEventListener(eventType, (event: MessageEvent) => {
          try {
            const data = JSON.parse(event.data as string) as Record<string, unknown>;

            if (eventType === 'error') {
              const msg = data['message'] as string | undefined;
              // "No active chat session" is a benign state (idle session, no agent
              // running). Close quietly without triggering reconnect attempts.
              if (msg === 'No active chat session') {
                eventSource.close();
                eventSourceRef.current = null;
                return;
              }
            }

            // Mark terminal events so the subsequent server-close doesn't retry.
            if (eventType === 'turn_complete' || eventType === 'error' || eventType === 'turn_aborted') {
              cleanTerminal = true;
            }

            setMessages((prev) => applySSEEvent(prev, eventType, data));
            onEventRef.current?.(eventType, data);
          } catch {
            // Ignore malformed SSE data
          }
        });
      }
    }

    openConnection();

    return () => {
      // Snapshot messages for Strict Mode recovery before teardown
      baseMessagesRef.current = messagesRef.current;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      setConnected(false);
    };
  }, [streamUrl, connectTrigger]);

  const reconnect = useCallback(() => {
    retryCountRef.current = 0;
    setError(null);
    setConnectTrigger((prev) => prev + 1);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const addMessage = useCallback((msg: SSEMessage) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  /** Remove the last seq-less (optimistic) user_message matching `content`.
   *  Only retracts messages that haven't been confirmed by the server (no seq).
   *  This prevents deleting canonical SSE-delivered messages when a network
   *  blip causes a client-side error after the server already accepted the send. */
  const removeLastUserMessage = useCallback((content: string) => {
    setMessages((prev) => {
      for (let i = prev.length - 1; i >= 0; i--) {
        const m = prev[i]!;
        if (m.type === 'user_message' && m.seq == null && m.content === content) {
          return [...prev.slice(0, i), ...prev.slice(i + 1)];
        }
      }
      return prev;
    });
  }, []);

  const initializeMessages = useCallback((msgs: SSEMessage[]) => {
    setMessages(msgs);
  }, []);

  const forceTurnAborted = useCallback((turnId: string, userMessage: string) => {
    setMessages((prev) => applySSEEvent(prev, 'turn_aborted', { turnId, userMessage }));
  }, []);

  return {
    messages,
    connected,
    error,
    clearMessages,
    reconnect,
    addMessage,
    removeLastUserMessage,
    initializeMessages,
    forceTurnAborted,
  };
}
